import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowLeft, Check, Loader2, Plane, Briefcase, Luggage, RefreshCw, Ban, Leaf, ShieldCheck } from 'lucide-react';
import { API_URL, money, hhmm, dayStr, sliceLabel, conditionText, CondRow, SliceHeader, extrasTotals } from './duffel/duffelShared';
import { DuffelExtras } from './duffel/DuffelExtras';
import { DuffelCheckout } from './duffel/DuffelCheckout';

const STAGES = ['fares', 'summary', 'extras', 'checkout'];
const TITLES = { fares: 'Choose your fare', summary: 'Booking summary', extras: 'Add extras', checkout: 'Passengers & payment' };
const EMPTY_SEL = { bags: {}, seats: {} };

const FareCard = ({ fare, selected, disabled, onSelect, sliceIndex }) => {
  const chg = conditionText(fare.change, 'Change');
  const rfd = conditionText(fare.refund, 'Refund');
  const slug = fare.brand.replace(/\s+/g, '-').toLowerCase();
  return (
    <button type="button" disabled={disabled} onClick={onSelect} data-testid={`fare-card-${sliceIndex}-${slug}`}
      className={`relative text-left rounded-xl border-2 p-4 transition-all w-full ${selected ? 'border-[#FF6600] bg-orange-50 shadow-md' : disabled ? 'border-slate-200 bg-slate-50 opacity-50 cursor-not-allowed' : 'border-slate-200 bg-white hover:border-orange-300 hover:shadow'}`}>
      {selected && <span className="absolute -top-2 -right-2 h-7 w-7 rounded-full bg-[#FF6600] text-white flex items-center justify-center shadow"><Check className="h-4 w-4" /></span>}
      <div className="flex items-baseline justify-between mb-3">
        <div className="font-bold text-slate-900 text-lg">{fare.brand}</div>
        <div className="text-right">
          <div className="text-xs text-slate-500">from</div>
          <div className="font-black text-[#FF6600] text-xl" data-testid={`fare-price-${sliceIndex}-${slug}`}>{money(fare.from_total)}</div>
        </div>
      </div>
      <div className="space-y-1.5">
        <CondRow icon={RefreshCw} ok={chg.ok} label={chg.label} detail={chg.detail} />
        <CondRow icon={Ban} ok={rfd.ok} label={rfd.label} detail={rfd.detail} />
        <CondRow icon={Briefcase} ok={fare.baggage.carry_on_included} label={fare.baggage.carry_on_included ? `${fare.baggage.carry_on_quantity} carry-on bag included` : 'Carry-on bag not included'} />
        <CondRow icon={Luggage} ok={fare.baggage.checked_included} label={fare.baggage.checked_included ? `${fare.baggage.checked_quantity} checked bag${fare.baggage.checked_quantity > 1 ? 's' : ''} included` : 'Checked bag not included'} />
      </div>
      {fare.cabin && <div className="mt-3 text-xs text-slate-500">{fare.cabin}</div>}
    </button>
  );
};

const SummaryCard = ({ data, slices, combo }) => (
  <Card data-testid="duffel-summary-card">
    <CardContent className="p-6 space-y-5">
      <div className="flex items-center gap-3"><Plane className="h-5 w-5 text-[#FF6600]" /><div className="font-bold text-slate-900">{data.airline} · {data.passenger_count} passenger{data.passenger_count !== 1 ? 's' : ''}</div></div>
      {slices.map((slice, i) => (
        <div key={slice.index} className="rounded-xl border border-slate-200 p-4">
          <SliceHeader slice={slice} label={sliceLabel(i)} />
          <div className="grid sm:grid-cols-2 gap-2 mt-2">
            <div className="text-sm"><span className="text-slate-500">Fare:</span> <strong>{combo.brands[i]}</strong></div>
            <div className="text-sm"><span className="text-slate-500">Baggage:</span> {combo.baggage[i].carry_on_included ? `${combo.baggage[i].carry_on_quantity} carry-on` : 'no carry-on'}{', '}{combo.baggage[i].checked_included ? `${combo.baggage[i].checked_quantity} checked bag${combo.baggage[i].checked_quantity > 1 ? 's' : ''}` : 'no checked bag'}</div>
          </div>
        </div>
      ))}
      <div className="grid sm:grid-cols-3 gap-3 text-sm">
        <div className="rounded-lg bg-slate-50 p-3"><div className="text-slate-500 text-xs uppercase">Changes</div>{conditionText(combo.change, 'Change').label}{combo.change?.penalty_amount ? ` · ${money(combo.change.penalty_amount, combo.change.penalty_currency)}` : ''}</div>
        <div className="rounded-lg bg-slate-50 p-3"><div className="text-slate-500 text-xs uppercase">Refunds</div>{conditionText(combo.refund, 'Refund').label}</div>
        <div className="rounded-lg bg-slate-50 p-3 flex items-start gap-2"><Leaf className="h-4 w-4 text-emerald-600 mt-0.5" /><div><div className="text-slate-500 text-xs uppercase">CO₂</div>{combo.co2_kg ? `${combo.co2_kg} kg per passenger` : 'Not supplied by airline'}</div></div>
      </div>
    </CardContent>
  </Card>
);

export const DuffelBookingFlow = ({ flight, onBack }) => {
  const storageKey = `duffelFareSelection:${flight.duffel_offer_id}`;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [stage, setStage] = useState('fares');
  const [selection, setSelection] = useState(() => { try { return JSON.parse(sessionStorage.getItem(storageKey) || '{}'); } catch { return {}; } });
  const [extras, setExtras] = useState(null);
  const [extrasError, setExtrasError] = useState(null);
  const [sel, setSel] = useState(EMPTY_SEL);

  useEffect(() => {
    fetch(`${API_URL}/api/duffel/offers/${flight.duffel_offer_id}/fares?offer_request_id=${flight.duffel_offer_request_id}`)
      .then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(j.detail || 'Unable to load fares'); return j; })
      .then(setData)
      .catch((e) => setError(e.message));
  }, [flight.duffel_offer_id, flight.duffel_offer_request_id]);

  useEffect(() => { sessionStorage.setItem(storageKey, JSON.stringify(selection)); }, [selection, storageKey]);

  const slices = data?.slices || [];
  const combos = data?.combos || [];
  const chosenBrands = slices.map((s) => selection[s.index]);
  const allChosen = slices.length > 0 && chosenBrands.every(Boolean);
  const activeCombo = useMemo(() => (allChosen ? combos.find((c) => c.brands.every((b, i) => b === chosenBrands[i])) : null), [allChosen, combos, chosenBrands]);
  const brandAvailable = (sliceIndex, brand) => combos.some((c) => c.brands[sliceIndex] === brand && c.brands.every((b, i) => i >= sliceIndex || !selection[i] || b === selection[i]));

  // extras belong to one specific offer — reset when the fare combination changes
  useEffect(() => { setExtras(null); setExtrasError(null); setSel(EMPTY_SEL); }, [activeCombo?.offer_id]);
  useEffect(() => {
    if (stage !== 'extras' && stage !== 'checkout') return;
    if (extras || extrasError || !activeCombo) return;
    fetch(`${API_URL}/api/duffel/offers/${activeCombo.offer_id}/extras`)
      .then(async (r) => { const j = await r.json(); if (!r.ok) throw new Error(j.detail || 'Unable to load extras'); return j; })
      .then(setExtras)
      .catch((e) => setExtrasError(e.message));
  }, [stage, extras, extrasError, activeCombo]);

  const pick = (sliceIndex, brand) => {
    setSelection((prev) => {
      const next = { ...prev, [sliceIndex]: brand };
      slices.forEach((s) => {
        if (s.index > sliceIndex && next[s.index] && !combos.some((c) => c.brands[sliceIndex] === brand && c.brands[s.index] === next[s.index])) delete next[s.index];
      });
      return next;
    });
  };

  if (error) {
    return (
      <div className="max-w-3xl mx-auto p-6 text-center" data-testid="duffel-fares-error">
        <p className="text-red-600 font-semibold mb-4">{error}</p>
        <Button onClick={onBack} variant="outline"><ArrowLeft className="h-4 w-4 mr-2" />Back to results</Button>
      </div>
    );
  }
  if (!data) return <div className="flex items-center justify-center py-24 text-slate-600" data-testid="duffel-fares-loading"><Loader2 className="h-6 w-6 animate-spin mr-3" />Loading fare options from {flight.airline}…</div>;

  const ex = extrasTotals(extras, sel);
  const fareTotal = activeCombo ? activeCombo.total_amount : slices.reduce((acc, s) => acc + (selection[s.index] ? (s.fares.find((f) => f.brand === selection[s.index])?.from_total || 0) : 0), 0);
  const grandTotal = Math.round((fareTotal + ex.total) * 100) / 100;
  const stageIdx = STAGES.indexOf(stage);
  const goBack = () => (stageIdx === 0 ? onBack() : setStage(STAGES[stageIdx - 1]));
  const next = () => { setStage(STAGES[stageIdx + 1]); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  return (
    <div className="max-w-6xl mx-auto px-4" data-testid="duffel-booking-flow">
      <Button variant="ghost" onClick={goBack} className="mb-4" data-testid="duffel-back-btn"><ArrowLeft className="h-4 w-4 mr-2" />{stageIdx === 0 ? 'Back to results' : `Back to ${TITLES[STAGES[stageIdx - 1]].toLowerCase()}`}</Button>

      <div className="flex items-center gap-2 mb-4 text-xs font-semibold" data-testid="duffel-stepper">
        {STAGES.map((s, i) => (
          <React.Fragment key={s}>
            {i > 0 && <span className={`h-px w-6 ${i <= stageIdx ? 'bg-[#FF6600]' : 'bg-slate-300'}`} />}
            <span className={`px-2.5 py-1 rounded-full ${i === stageIdx ? 'bg-[#FF6600] text-white' : i < stageIdx ? 'bg-orange-100 text-[#FF6600]' : 'bg-slate-100 text-slate-500'}`}>{i + 1}. {TITLES[s]}</span>
          </React.Fragment>
        ))}
      </div>

      <div className="grid lg:grid-cols-[1fr_340px] gap-6">
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            {data.airline_logo && <img src={data.airline_logo} alt={data.airline} className="h-8" />}
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">{TITLES[stage]}</h1>
            <Badge className="bg-[#FF6600] text-white">Low-cost</Badge>
            {!data.live_mode && <Badge variant="outline" className="text-amber-600 border-amber-300">Test fares</Badge>}
          </div>

          {stage === 'fares' && slices.map((slice, i) => {
            const locked = i > 0 && !selection[i - 1];
            return (
              <Card key={slice.index} className={locked ? 'opacity-50' : ''} data-testid={`fare-slice-${slice.index}`}>
                <CardContent className="p-5">
                  <SliceHeader slice={slice} label={sliceLabel(i)} />
                  {locked ? <p className="text-sm text-slate-500">Select your outbound fare first.</p> : (
                    <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
                      {slice.fares.map((fare) => <FareCard key={fare.brand} fare={fare} sliceIndex={slice.index} selected={selection[slice.index] === fare.brand} disabled={!brandAvailable(slice.index, fare.brand)} onSelect={() => pick(slice.index, fare.brand)} />)}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {stage === 'summary' && activeCombo && <SummaryCard data={data} slices={slices} combo={activeCombo} />}

          {(stage === 'extras' || stage === 'checkout') && extrasError && (
            <Card><CardContent className="p-6 text-center" data-testid="duffel-extras-error"><p className="text-red-600 font-semibold mb-3">{extrasError}</p><Button variant="outline" onClick={() => setStage('fares')}>Choose fare again</Button></CardContent></Card>
          )}
          {(stage === 'extras' || stage === 'checkout') && !extras && !extrasError && (
            <div className="flex items-center justify-center py-16 text-slate-600" data-testid="duffel-extras-loading"><Loader2 className="h-6 w-6 animate-spin mr-3" />Loading baggage and seat options…</div>
          )}
          {stage === 'extras' && extras && <DuffelExtras extras={extras} slices={slices} sel={sel} setSel={setSel} />}
          {stage === 'checkout' && extras && activeCombo && <DuffelCheckout flight={flight} fares={data} combo={activeCombo} extras={extras} sel={sel} total={grandTotal} />}
        </div>

        <aside className="lg:sticky lg:top-28 self-start">
          <Card className="border-2 border-orange-200" data-testid="duffel-summary-rail">
            <CardContent className="p-5 space-y-4">
              <div className="font-bold text-slate-900 text-lg">Summary</div>
              {slices.map((slice, i) => (
                <div key={slice.index} className="flex items-start justify-between text-sm border-b border-slate-100 pb-3">
                  <div>
                    <div className="font-semibold text-slate-800">{sliceLabel(i)} · {slice.origin}→{slice.destination}</div>
                    <div className="text-slate-500">{dayStr(slice.departing_at)} · {hhmm(slice.departing_at)}</div>
                    <div className={selection[slice.index] ? 'text-[#FF6600] font-semibold' : 'text-slate-400'} data-testid={`summary-brand-${slice.index}`}>{selection[slice.index] || 'Choose a fare'}</div>
                  </div>
                </div>
              ))}
              <div className="space-y-1 text-sm">
                <div className="flex justify-between text-slate-600"><span>Fares ({data.passenger_count} pax)</span><span data-testid="rail-fare-total">{fareTotal ? money(fareTotal) : '—'}</span></div>
                {ex.bags > 0 && <div className="flex justify-between text-slate-600"><span>Extra bags</span><span data-testid="rail-bags-total">{money(ex.bags)}</span></div>}
                {ex.seats > 0 && <div className="flex justify-between text-slate-600"><span>Seats</span><span data-testid="rail-seats-total">{money(ex.seats)}</span></div>}
              </div>
              <div className="flex items-baseline justify-between border-t border-slate-200 pt-3">
                <span className="text-slate-600">Total</span>
                <span className="text-2xl font-black text-slate-900" data-testid="duffel-summary-total">{grandTotal ? money(grandTotal) : '—'}</span>
              </div>
              {allChosen && !activeCombo && <p className="text-xs text-red-600">This fare combination isn't offered by the airline — pick another inbound fare.</p>}
              {stage === 'fares' && <Button className="w-full h-12 bg-[#FF6600] hover:bg-[#e65c00] text-white font-bold" disabled={!activeCombo} onClick={next} data-testid="duffel-continue-btn">Continue to summary</Button>}
              {stage === 'summary' && <Button className="w-full h-12 bg-[#FF6600] hover:bg-[#e65c00] text-white font-bold" onClick={next} data-testid="duffel-checkout-btn">Add extras</Button>}
              {stage === 'extras' && (
                <>
                  <Button className="w-full h-12 bg-[#FF6600] hover:bg-[#e65c00] text-white font-bold" disabled={!extras} onClick={next} data-testid="duffel-to-checkout-btn">Continue to checkout</Button>
                  {extras && ex.total === 0 && <p className="text-center text-xs text-slate-500">No extras selected — you can continue without bags or seats.</p>}
                </>
              )}
              {stage === 'checkout' && <p className="text-xs text-slate-500 text-center">Complete passenger details below to pay.</p>}
              <div className="flex items-center gap-2 text-xs text-slate-500"><ShieldCheck className="h-4 w-4 text-emerald-600" />Prices include all taxes · Fare rules supplied by {data.airline}</div>
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
};
